import '@fontsource/lato';
import '@fontsource/roboto';
import { alpha, createTheme, LinkProps, ThemeProvider } from '@mui/material';
import { grey } from '@mui/material/colors';
import { PropsWithChildren } from 'react';
import HenrietteBlackWoff2 from 'shared/fonts/Henriette-Black.woff2';
import RouterLinkComponent from 'shared/Providers/MuiTheme/components/RouterLinkComponent';
import DESIGN_COLORS from 'shared/_ui/design-colors';
import { IconCheckboxBlank, IconCheckboxChecked, IconCheckboxIndeterminate } from 'shared/_ui/icon';

const borderRadius = 10;

/** Border color of an unchecked checkbox (new design: slate-300 #d1d5db). */
const CHECKBOX_UNCHECKED_COLOR = '#d1d5db';

/** Track color of a checked default/primary switch (new design: green). */
const SWITCH_ON_COLOR = '#10b981';
/** Track color of an unchecked switch (new design: slate-200). */
const SWITCH_OFF_COLOR = '#e2e8f0';
/** Same duration as the design's transition. */
const SWITCH_TRANSITION = '0.2s';

/**
 * Geometry of one switch size. All values are `theme.spacing()` units (0.25rem each in this app):
 * track `width` x `height`, knob `thumb` size and the `inset` gap between knob and track.
 */
function switchSize(width: number, height: number, thumb: number, inset: number) {
	const travel = theme.spacing(width - thumb - inset * 2);
	return {
		width: theme.spacing(width),
		height: theme.spacing(height),
		padding: 0,
		'& .MuiSwitch-switchBase': {
			padding: theme.spacing(inset),
			'&.Mui-checked': { transform: `translateX(${travel})` },
			// Keep the invisible input covering the whole track (it moves together with the knob)
			'& .MuiSwitch-input': { left: 0, width: theme.spacing(width) },
			'&.Mui-checked .MuiSwitch-input': { left: `calc(-1 * ${travel})` },
		},
		'& .MuiSwitch-thumb': {
			width: theme.spacing(thumb),
			height: theme.spacing(thumb),
		},
	};
}

export const FONT_FAMILY_HEADER = ['"Henriette Black"', 'Georgia', 'serif'].join(',');

const FONT_FAMILY_MONO = '"SF Mono", Monaco, monospace';

const FONT_FAMILY_LATO = [
	'"Lato"',
	'-apple-system',
	'BlinkMacSystemFont',
	'"Segoe UI"',
	'Helvetica',
	'Arial',
	'sans-serif',
	'"Apple Color Emoji"',
	'"Segoe UI Emoji"',
	'"Segoe UI Symbol"',
].join(',');

// --bgColor: white;
// --btnPrimaryBg: black;
// --btnPrimaryText: white;
// --btnSecondaryBg: #d1d3d4;
// --btnSecondaryText: white;
// --btnWarningBg: #bf1e2d;
// --btnWarningText: white;
// --btnTransparentBg: #00000000;
// --btnTransparentText: black;
// --btnNoneText: black;
// --btnNoneHover: #eee;
// --textColor: #000;
// --lightText: #9e9e9e;
// --bgHover: #00000019;
// --bg2ndRow: #00000008;
// --disabledOpacity: 0.5;
// --inputValidateFlagWidth: 20px
// d8d8d8

const lineHeight = {
	// xs: 1.1,
	// sm: 1.2,
	md: '1.3',
	// lg: 1.4,
	// xlg: 1.5,
} as const;

/** New design muted text (`--nd-text-muted`, slate-500). */
export const colorLabel = '#64748b';

// TODO: Why do I need to add fontSize everywhere?
let theme = createTheme({
	spacing: (factor: number) => `${0.25 * factor}rem`,
	palette: {
		mode: 'light',
		primary: {
			// New design strong text / primary button background (`--nd-text-strong`, slate-900)
			main: '#0f172a',
			dark: grey[600],
		},
		secondary: {
			// New design faint text (`--nd-text-faint`, slate-400)
			main: '#94a3b8',
			dark: grey[200],
		},
		text: {
			// New design regular text (`--nd-text`, slate-800) and muted text (`--nd-text-muted`, slate-500)
			primary: '#1e293b',
			secondary: '#64748b',
		},
		background: {
			// New design page background (`--nd-bg`, slate-50); cards and lists stay white (`paper`)
			default: '#f8fafc',
		},
		// contrastThreshold: 4.5,
		// tonalOffset: 0.2,
	},
});

type PartialDeep<T> = {
	// eslint-disable-next-line @typescript-eslint/ban-types
	[K in keyof T]?: T[K] extends object ? PartialDeep<T[K]> : T[K];
};

theme = createTheme(theme, {
	palette: {
		...theme.palette,
		label: theme.palette.augmentColor({
			color: {
				main: colorLabel,
			},
			name: 'label',
		}),
		success: theme.palette.augmentColor({
			color: {
				main: '#00a14b',
			},
			name: 'success',
		}),
	},
});

theme = createTheme(theme, {
	typography: {
		allVariants: {
			fontFamily: FONT_FAMILY_LATO,
		},
		fontFamily: FONT_FAMILY_LATO,
		// Monospace stack for IDs and other code-like text: `theme.typography.fontFamilyMono`
		fontFamilyMono: FONT_FAMILY_MONO,
		fontSize: 14,
		lineHeight: lineHeight.md,
		body1: {
			fontSize: '0.875rem', // 14px
			// [theme.breakpoints.up('md')]: {
			// 	fontSize: '1rem', // 16px
			// },
		},
		body2: {
			fontSize: '0.875rem', // 14px
			color: theme.palette.secondary.main,
			// [theme.breakpoints.up('md')]: {
			// 	fontSize: '1rem', // 16px
			// },
		},
		h1: {
			textAlign: 'left',
			fontFamily: FONT_FAMILY_HEADER,
			fontWeight: 900,
			lineHeight: 1.4,
			textTransform: 'capitalize',
			fontSize: '1.25rem',
			[theme.breakpoints.up('md')]: {
				fontSize: '1.75rem',
			},
		},
		h2: {
			fontFamily: FONT_FAMILY_HEADER,
			fontSize: 31,
		},
		h3: {
			fontWeight: 'bold',
			fontSize: 22,
		},
		h4: {
			fontSize: 18,
			fontWeight: 'bold',
		},
		h5: {
			fontSize: 16,
			fontWeight: 'bold',
		},
		h6: {
			fontSize: 14,
			fontWeight: 'bold',
		},
		button: {
			fontFamily: FONT_FAMILY_LATO,
			fontSize: '0.8125rem',
			fontWeight: 700,
			lineHeight: 'normal',
			textTransform: 'none',
		},
	},
	components: {
		MuiFormLabel: {
			styleOverrides: {
				root: {
					fontFamily: FONT_FAMILY_LATO,
				},
			},
		},
	},
});

theme = createTheme(theme, {
	components: {
		MuiAppBar: {
			styleOverrides: {
				root: {
					borderRadius: 0,
					// backgroundColor: theme.palette.common.white,
					// color: theme.palette.primary.main,
				},
			},
		},
		MuiBadge: {
			defaultProps: {
				color: 'primary',
			},
			variants: [
				{
					props: { variant: 'outlined' },
					style: {
						height: 'fit-content',
						'.MuiBadge-badge': {
							border: '0.1rem solid black',
							color: theme.palette.primary.main, // 'black', // TODO: Use theme constants
							backgroundColor: theme.palette.background.default, // 'white', // TODO: Use theme constants
							padding: `${theme.spacing(4 * 0.05)} ${theme.spacing(4 * 0.22)} 0`, // '0.05rem 0.22rem 0',
							fontSize: '0.5rem', // 6pt // TODO: Use theme constants
							// fontSize: '25cqh', // TODO: Update to Chromium v110
							height: 'min-content',
							lineHeight: '1.34',
							textTransform: 'none',
							transform: 'none',
							minWidth: 'unset',
							fontWeight: '900',
						},
						'.MuiBadge-badge.MuiBadge-invisible': {
							transform: 'scale(0) translate(50%, 50%)',
						},
					},
				},
				{
					props: { variant: 'component' },
					style: {
						'.MuiBadge-badge': {
							backgroundColor: 'transparent',
							padding: 0,
						},
					},
				},
			],
			styleOverrides: {
				root: {
					whiteSpace: 'nowrap',
				},
			},
		},
		MuiButton: {
			defaultProps: {
				variant: 'contained',
				size: 'medium',
			},
			styleOverrides: {
				root: ({ ownerState }) => ({
					whiteSpace: 'nowrap',
					borderRadius,
					gap: theme.spacing(1.5),
					...(ownerState.fullWidth
						? {}
						: {
								minWidth: 'unset',
								width: 'max-content',
						  }),
					height: 'max-content',
				}),
				startIcon: {
					marginRight: 0,
					marginLeft: 0,
				},
				endIcon: {
					marginRight: 0,
					marginLeft: 0,
				},
			},
			variants: [
				// New design "outline" button: white fill, thin border. This is how the default secondary looks.
				{
					props: { variant: 'contained', color: 'secondary' },
					style: {
						backgroundColor: theme.palette.background.paper,
						color: theme.palette.text.primary,
						border: `1px solid ${DESIGN_COLORS.border}`,
						'&:hover': {
							backgroundColor: theme.palette.background.paper,
							borderColor: DESIGN_COLORS.borderHover,
						},
						'&.Mui-disabled': {
							backgroundColor: theme.palette.background.paper,
							color: theme.palette.text.primary,
							opacity: 0.5,
						},
					},
				},
				{
					props: { size: 'large' },
					style: {
						fontSize: '0.9375rem',
						minHeight: 'calc(1.4em)',
						'.MuiButton-startIcon': {
							'&>*:nth-of-type(1)': {
								fontSize: '1rem',
							},
						},
						'.MuiButton-endIcon': {
							'&>*:nth-of-type(1)': {
								fontSize: '1rem',
							},
						},
					},
				},
				{
					props: { size: 'medium' },
					style: {
						fontSize: '0.875rem',
						// padding: theme.spacing(2),
						minHeight: 'calc(1.4em)',
						'.MuiButton-startIcon': {
							'&>*:nth-of-type(1)': {
								// fontSize: '0.75rem',
								fontSize: '1rem',
							},
						},
						'.MuiButton-endIcon': {
							'&>*:nth-of-type(1)': {
								fontSize: '1rem',
							},
						},
					},
				},
				{
					props: { size: 'small' },
					style: {
						fontSize: '0.8125rem',
						// fontSize: '0.75rem',
						// padding: theme.spacing(2),
						// minHeight: 'calc(1.4em)',
						'.MuiButton-startIcon': {
							'&>*:nth-of-type(1)': {
								// fontSize: '0.75rem',
								fontSize: '0.75rem',
							},
						},
						'.MuiButton-endIcon': {
							'&>*:nth-of-type(1)': {
								fontSize: '0.75rem',
							},
						},
					},
				},
			],
		},
		MuiButtonBase: {
			defaultProps: {
				LinkComponent: RouterLinkComponent,
			},
		},
		MuiButtonGroup: {
			defaultProps: {
				variant: 'contained',
			},
			styleOverrides: {
				root: {
					borderRadius,
				},
			},
		},
		// New design: rounded-square box (radius 4px, 2px border) with a solid fill and a cut-out check when
		// checked. Icons are drawn so that the default size (`small`, 24px icon) renders the exact 16px box
		// from the design. Note: the spacing function here is rem-based (0.25rem per unit).
		MuiCheckbox: {
			defaultProps: {
				size: 'small',
				icon: <IconCheckboxBlank />,
				checkedIcon: <IconCheckboxChecked />,
				indeterminateIcon: <IconCheckboxIndeterminate />,
			},
			styleOverrides: {
				root: ({ ownerState }) => {
					const color = (ownerState as Record<string, unknown>).color ?? 'primary';
					return {
						// Unchecked border color (new design: slate-300)
						...(color === 'default' || color === 'primary' ? { color: CHECKBOX_UNCHECKED_COLOR } : {}),
						...(ownerState.disabled ? { opacity: 0.6 } : {}),
					};
				},
				sizeSmall: ({ theme }) => ({
					// Equal padding on all sides keeps the hover halo a circle (MUI default is 9px horizontally)
					padding: theme.spacing(1.5), // 6px
					'& .MuiSvgIcon-fontSizeSmall': {
						fontSize: theme.spacing(6), // 24px icon -> 16px box
					},
				}),
				sizeMedium: ({ theme }) => ({
					padding: theme.spacing(1.5), // 6px
					'& .MuiSvgIcon-fontSizeMedium': {
						fontSize: theme.spacing(7.5), // 30px icon -> 20px box
					},
				}),
			},
		},
		MuiChip: {
			defaultProps: {
				size: 'small',
			},
			variants: [
				{
					props: { color: 'default' },
					style: {
						'.MuiChip-label': {
							color: theme.palette.primary.main,
						},
					},
				},
			],
		},
		MuiCssBaseline: {
			styleOverrides: `
        @font-face {
          font-family: 'Henriette Black';
          font-style: normal;
          font-display: swap;
          font-weight: 900;
          src: local('Henriette'), local('Henriette Black'), url(${HenrietteBlackWoff2}) format('woff2');
          unicode-range: U+000D-FB04;
        }
      `,
		},
		MuiDialogActions: {
			styleOverrides: {
				root: {
					paddingInline: theme.spacing(6),
					paddingBottom: theme.spacing(6),
				},
			},
		},
		MuiDrawer: {
			styleOverrides: {
				paper: {
					borderRadius: 0,
					border: 'none',
				},
			},
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					color: colorLabel,
				},
			},
		},
		MuiFormControl: {
			styleOverrides: {
				root: ({ ownerState, ...rest }) => {
					// // @ts-ignore
					// console.log('tst: MuiFormControl', rest?.children?.[0]?.props?.htmlFor, { ownerState, rest });
					return {
						'&.field': {
							flexDirection: 'row',
							columnGap: theme.spacing(1),
							'&.editable': {},
							'.MuiFormLabel-root': {
								position: 'unset',
								transform: 'none',
								alignItems: 'center',
								display: 'inline-flex',
								flex: 'none',
								alignSelf: 'start',
								paddingBlock: theme.spacing(2),
							},
							'.MuiFormControlLabel-root': {
								// TODO: What this for?
								flex: ownerState.fullWidth ? 1 : undefined,
								'.MuiFormControlLabel-label': {
									flex: ownerState.fullWidth ? 1 : undefined,
								},
								marginLeft: 0,
								marginRight: '-8px',
								// The switch has no built-in padding: keep it aligned with the text of neighbour inputs
								'&:has(.MuiSwitch-root)': {
									marginRight: 0,
									'.MuiSwitch-root': { marginLeft: theme.spacing(2) },
								},
							},
							'.MuiTypography-root': {
								padding: theme.spacing(2),
							},
							'.MuiInputBase-input': {
								padding: 0,
								// restore hidden placeholder
								'&[placeholder]:not([readonly])::-webkit-input-placeholder': {
									opacity: '0.5!important',
								},
								// hide placeholder
								'&[placeholder][readonly]::-webkit-input-placeholder': {
									opacity: '0!important',
								},
							},
							'.MuiInputBase-input:not(textarea)': {
								'&.MuiInputBase-readOnly': {
									overflow: 'hidden',
									whiteSpace: 'nowrap',
									textOverflow: 'ellipsis',
								},
							},
							'.MuiInputBase-root.MuiInputBase-readOnly': {
								'> fieldset': {
									border: 'none',
								},
							},
							'.MuiInputBase-root': {
								padding: theme.spacing(2),
							},
							'.MuiInputBase-root > fieldset': {
								top: 0,
								legend: {
									display: 'none',
								},
							},
						},
					};
				},
			},
		},
		MuiFormControlLabel: {
			styleOverrides: {
				root: {
					color: colorLabel,
				},
			},
		},
		MuiToggleButton: {
			defaultProps: {
				size: 'small',
			},
		},
		// New design: headings use the strong text color. A `color` prop / `sx` color still overrides it.
		MuiTypography: {
			variants: (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const).map((variant) => ({
				props: { variant },
				style: { color: theme.palette.primary.main },
			})),
		},
		MuiIconButton: {
			defaultProps: {
				color: 'primary',
			},
			styleOverrides: {
				root: {
					height: 'fit-content',
				},
			},
			variants: [
				// New design page-header icon button (share, more): square, white fill, thin border.
				{
					props: { color: 'secondary' },
					style: {
						width: '2.375rem',
						height: '2.375rem',
						padding: 0,
						border: `1px solid ${DESIGN_COLORS.border}`,
						borderRadius: '0.5625rem',
						backgroundColor: theme.palette.background.paper,
						color: theme.palette.text.secondary,
						'&:hover': {
							borderColor: DESIGN_COLORS.borderHover,
							backgroundColor: DESIGN_COLORS.surfaceMuted,
							color: theme.palette.primary.main,
						},
						'&.Mui-disabled': { opacity: 0.5 },
					},
				},
				{
					props: { size: 'inherit' },
					style: {
						fontSize: 'inherit',
						'.MuiSvgIcon-root': {
							fontSize: 'inherit',
						},
					},
				},
			],
		},
		MuiInput: {
			styleOverrides: {
				root: {
					fontSize: '0.875rem',
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					color: 'currentColor',
				},
			},
		},
		MuiLink: {
			defaultProps: {
				component: RouterLinkComponent,
				underline: 'hover',
			} as LinkProps,
		},
		MuiList: {
			defaultProps: {
				color: 'primary',
			},
			// styleOverrides
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					fontSize: '0.875rem',
					// Menu text is `text.primary` (the Paper default is `primary.main`); icons follow the text color.
					color: theme.palette.text.primary,
					'& .MuiListItemIcon-root': { color: 'inherit' },
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					borderRadius,
					'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
						borderColor: theme.palette.primary.main,
						borderWidth: '1px',
						boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					color: theme.palette.primary.main, // 'black', // TODO: Use theme constants
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					borderRadius,
					border: '1px solid',
					borderColor: grey[300],
					'.MuiCardHeader-avatar': {
						marginRight: theme.spacing(2),
					},
					'.MuiCardHeader-title': {
						...theme.typography.h4,
						// fontFamily: theme.typography.body1.fontFamily,
						color: theme.palette.primary.main,
					},
					'.MuiCardContent-root:last-child': {
						paddingBottom: theme.spacing(4),
					},
				},
			},
		},
		MuiDialog: {
			styleOverrides: {
				paper: {
					borderRadius,
				},
			},
		},
		// New design ("Toggle"): 36x20 pill, grey track when off, green when on, white knob with a soft shadow.
		// The default size (`medium` in this app) renders the design's exact dimensions; `small` is scaled down (28x16).
		// Non-primary colors (`secondary`, `success`, `error`, ...) tint the checked track with their palette color.
		MuiSwitch: {
			defaultProps: {
				size: 'medium',
				disableRipple: true,
			},
			styleOverrides: {
				root: {
					overflow: 'visible', // lets the focus ring render outside the track
					// Fade the whole switch (track + knob together), also when disabled via FormControl
					'&:has(.Mui-disabled)': { opacity: 0.5 },
				},
				sizeMedium: switchSize(9, 5, 4, 0.5), // 36x20, knob 16
				sizeSmall: switchSize(7, 4, 3, 0.5), // 28x16, knob 12
				switchBase: ({ ownerState }) => {
					const color = (ownerState as Record<string, unknown>).color ?? 'primary';
					return {
						transition: `transform ${SWITCH_TRANSITION}`,
						// No hover halo
						'&:hover, &.Mui-checked:hover': { backgroundColor: 'transparent' },
						// Track keeps full opacity in every state; disabled is faded on the root instead
						'&.Mui-checked + .MuiSwitch-track, &.Mui-disabled + .MuiSwitch-track': { opacity: 1 },
						...(color === 'primary' || color === 'default'
							? { '&.Mui-checked + .MuiSwitch-track': { opacity: 1, backgroundColor: SWITCH_ON_COLOR } }
							: {}),
						'&.Mui-focusVisible + .MuiSwitch-track': {
							boxShadow: '0 0 0 2px #ffffff, 0 0 0 4px rgba(100, 116, 139, 0.55)',
						},
					};
				},
				thumb: {
					backgroundColor: '#ffffff',
					boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
				},
				track: {
					border: 'none',
					borderRadius: 9999,
					opacity: 1,
					backgroundColor: SWITCH_OFF_COLOR,
					transition: `background-color ${SWITCH_TRANSITION}`,
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: ({ ownerState }) => {
					const readOnly = !!(ownerState as { slotProps?: { input?: { readOnly?: boolean } } })
						.slotProps?.input?.readOnly;
					const hasOnClick = !!ownerState.onClick;

					return {
						'.MuiInputBase-root': hasOnClick
							? {
									transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
									cursor: 'pointer',
									'& input, & textarea': {
										cursor: 'pointer',
									},
									'&:hover': {
										backgroundColor: 'rgba(0,0,0,0.04)',
									},
							  }
							: {},
						'&.field .MuiFormLabel-root': {
							'--lineHeight': lineHeight.md,
							lineHeight: 'var(--lineHeight)',
							paddingTop: readOnly ? undefined : theme.spacing(2),
						},
						'.MuiInputBase-input': {
							lineHeight: 'var(--lineHeight)',
						},
						'input.MuiInputBase-input': {
							height: 'calc(var(--lineHeight)em)',
						},
						'.MuiInputAdornment-positionEnd': {
							marginRight: theme.spacing(1),
						},
					};
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					borderBottom: '1px solid',
					borderColor: theme.palette.divider,
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					fontSize: '1rem',
					fontWeight: 'bold',
				},
			},
		},
	},
} as PartialDeep<typeof theme>);

declare module '@mui/material/styles' {
	interface TypographyVariants {
		fontFamilyMono: string;
	}
	interface TypographyVariantsOptions {
		fontFamilyMono?: string;
	}
}

declare module '@mui/material/Badge' {
	interface BadgePropsVariantOverrides {
		outlined: true;
		component: true;
	}
}
declare module '@mui/material/IconButton' {
	interface IconButtonPropsSizeOverrides {
		inherit: true;
	}
}

export const MuiThemeProvider = ({ children }: PropsWithChildren): JSX.Element => (
	<ThemeProvider theme={theme}>{children}</ThemeProvider>
);
